import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  Button,
  Stack,
  Textarea,
  SimpleGrid,
  useToast,
  Icon,
} from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  FaLandmark,
  FaUserShield,
  FaTools,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { MdInfo, MdImage, MdLocationOn } from 'react-icons/md';
import axios from 'axios';
import baseUrl from '../../../Base_Url/baseUrl';
import { useDispatch } from 'react-redux';
import { fetchSurveyStats } from '../../../redux/slices/surveyStatsSlice';
import { Image } from '@chakra-ui/react';
import { FaFileSignature } from 'react-icons/fa6';
import { useColorModeValue } from '@chakra-ui/react';
import { useColorMode } from '@chakra-ui/react';

const hasValue = (value) =>
  value !== null && value !== undefined && value !== '';

// Capitalize function
const capitalizeFirst = (text) => {
  if (!text || typeof text !== 'string') return 'N/A';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const SurveyDetailsModal = ({
  isOpen,
  onClose,
  selectedSurvey,
  user,
  fetchSurveys,
  comparisonData,
}) => {
  const toast = useToast();
  const [remark, setRemark] = useState('');
  const dispatch = useDispatch();

  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const approveSurvey = async () => {
    setIsApproving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${baseUrl}/survey/update-multiple`,
        {
          surveyIds: [selectedSurvey._id],
          status: 'approved',
          ...(remark && { remark }),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.code === 200) {
        toast({
          title: 'Survey approved successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }

      fetchSurveys();
      dispatch(fetchSurveyStats());
      onClose();
    } catch (error) {
      toast({
        title: 'Failed to approve the survey.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsApproving(false);
    }
  };

  const rejectSurvey = async () => {
    if (!remark.trim()) {
      toast({
        title: 'Remark is required for rejection.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsRejecting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${baseUrl}/survey/update-multiple`,
        {
          surveyIds: [selectedSurvey._id],
          status: 'rejected',
          remark: remark.trim(),
          rejectionComment: remark.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.code === 200) {
        toast({
          title: 'Survey rejected successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }

      fetchSurveys();
      onClose();
      dispatch(fetchSurveyStats());
    } catch (error) {
      toast({
        title: 'Failed to reject the survey.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRejecting(false);
    }
  };

  const modalHeaderBg = useColorModeValue(
    'linear-gradient(to right, #4299e1, #3182ce)',
    'linear-gradient(to right, #2a4365, #1A365D)',
  );
  const modalHeaderColor = useColorModeValue('white', 'gray.100');
  const sectionHeadingColor = useColorModeValue('blue.500', 'blue.300');
  const sectionBoxBg = useColorModeValue('gray.50', 'gray.700');
  const sectionTextColor = useColorModeValue('gray.800', 'gray.100');
  const sectionBorderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('green.600', 'green.300');
  const sectionTitleColor = useColorModeValue('orange.500', 'orange.300');
  const boxBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const previousColor = useColorModeValue('red.500', 'red.300');
  const linkColor = useColorModeValue('blue.600', 'blue.300');
  const mapLinkColor = useColorModeValue('green.600', 'green.300');
  const sectionRedTitleColor = useColorModeValue('red.500', 'red.300');
  const hrColor = useColorModeValue('black', 'gray.300');
  const inputBg = useColorModeValue('white', 'gray.600');
  const placeholderColor = useColorModeValue('gray.500', 'gray.300');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="900px" mx="auto" rounded="lg" shadow="lg">
        <Box
          bg={modalHeaderBg}
          color={modalHeaderColor}
          py={2}
          textAlign="center"
          roundedTop="lg"
        >
          <ModalHeader fontSize="2xl" fontWeight="bold">
            <Icon as={InfoOutlineIcon} mr={2} />
            SURVEY DETAILS
          </ModalHeader>
          <ModalCloseButton color={modalHeaderColor} />
        </Box>

        <ModalBody>
          {selectedSurvey ? (
            <Box p={4}>
              <Box mb={6}>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  mb={4}
                  color={sectionHeadingColor}
                >
                  <Icon as={FaLandmark} mr={2} />
                  Basic Land Information
                </Text>
                <Box
                  bg={sectionBoxBg}
                  p={4}
                  rounded="md"
                  shadow="md"
                  color={sectionTextColor}
                  borderColor={sectionBorderColor}
                >
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontWeight="bold">Division:</Text>
                      <Text>
                        {capitalizeFirst(
                          selectedSurvey.division?.name || 'N/A',
                        )}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">District:</Text>
                      <Text>
                        {capitalizeFirst(
                          selectedSurvey.district?.name || 'N/A',
                        )}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Taluka:</Text>
                      <Text>
                        {capitalizeFirst(selectedSurvey.tahsil?.name || 'N/A')}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Village:</Text>
                      <Text>
                        {capitalizeFirst(selectedSurvey.village?.name || 'N/A')}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Gat No:</Text>
                      <Text>{selectedSurvey.gatNumber || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Total Area:</Text>
                      {selectedSurvey.status === 'pending' &&
                      comparisonData &&
                      (comparisonData.totalAreaInHec !==
                        selectedSurvey.totalAreaInHec ||
                        comparisonData.totalAreaInSqft !==
                          selectedSurvey.totalAreaInSqft ||
                        comparisonData.totalAreaInSqmt !==
                          selectedSurvey.totalAreaInSqmt) ? (
                        <>
                          <Text>
                            {[
                              selectedSurvey.totalAreaInHec &&
                                `${selectedSurvey.totalAreaInHec} ha`,
                              selectedSurvey.totalAreaInSqft &&
                                `${selectedSurvey.totalAreaInSqft} sq.ft`,
                              selectedSurvey.totalAreaInSqmt &&
                                `${selectedSurvey.totalAreaInSqmt} sq.mt`,
                            ]
                              .filter(Boolean)
                              .join(' / ')}
                          </Text>
                          {user?.role === 'tahsildar' &&
                            (comparisonData.totalAreaInHec ||
                              comparisonData.totalAreaInSqft ||
                              comparisonData.totalAreaInSqmt) && (
                              <Text color={previousColor}>
                                <strong>Previous:</strong>{' '}
                                {[
                                  comparisonData.totalAreaInHec &&
                                    `${comparisonData.totalAreaInHec} ha`,
                                  comparisonData.totalAreaInSqft &&
                                    `${comparisonData.totalAreaInSqft} sq.ft`,
                                  comparisonData.totalAreaInSqmt &&
                                    `${comparisonData.totalAreaInSqmt} sq.mt`,
                                ]
                                  .filter(Boolean)
                                  .join(' / ')}
                              </Text>
                            )}
                        </>
                      ) : (
                        <Text>
                          {[
                            selectedSurvey.totalAreaInHec &&
                              `${selectedSurvey.totalAreaInHec} ha`,
                            selectedSurvey.totalAreaInSqft &&
                              `${selectedSurvey.totalAreaInSqft} sq.ft`,
                            selectedSurvey.totalAreaInSqmt &&
                              `${selectedSurvey.totalAreaInSqmt} sq.mt`,
                          ]
                            .filter(Boolean)
                            .join(' / ')}
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Encroached Area:</Text>
                      {selectedSurvey.status === 'pending' &&
                      comparisonData &&
                      (comparisonData.encroachedAreaInHec !==
                        selectedSurvey.encroachedAreaInHec ||
                        comparisonData.encroachedAreaInSqft !==
                          selectedSurvey.encroachedAreaInSqft ||
                        comparisonData.encroachedAreaInSqmt !==
                          selectedSurvey.encroachedAreaInSqmt) ? (
                        <>
                          <Text>
                            {[
                              selectedSurvey.encroachedAreaInHec &&
                                `${selectedSurvey.encroachedAreaInHec} ha`,
                              selectedSurvey.encroachedAreaInSqft &&
                                `${selectedSurvey.encroachedAreaInSqft} sq.ft`,
                              selectedSurvey.encroachedAreaInSqmt &&
                                `${selectedSurvey.encroachedAreaInSqmt} sq.mt`,
                            ]
                              .filter(Boolean)
                              .join(' / ')}
                          </Text>
                          {user?.role === 'tahsildar' &&
                            (comparisonData.encroachedAreaInHec ||
                              comparisonData.encroachedAreaInSqft ||
                              comparisonData.encroachedAreaInSqmt) && (
                              <Text color={previousColor}>
                                <strong>Previous:</strong>{' '}
                                {[
                                  comparisonData.encroachedAreaInHec &&
                                    `${comparisonData.encroachedAreaInHec} ha`,
                                  comparisonData.encroachedAreaInSqft &&
                                    `${comparisonData.encroachedAreaInSqft} sq.ft`,
                                  comparisonData.encroachedAreaInSqmt &&
                                    `${comparisonData.encroachedAreaInSqmt} sq.mt`,
                                ]
                                  .filter(Boolean)
                                  .join(' / ')}
                              </Text>
                            )}
                        </>
                      ) : (
                        <Text>
                          {[
                            selectedSurvey.encroachedAreaInHec &&
                              `${selectedSurvey.encroachedAreaInHec} ha`,
                            selectedSurvey.encroachedAreaInSqft &&
                              `${selectedSurvey.encroachedAreaInSqft} sq.ft`,
                            selectedSurvey.encroachedAreaInSqmt &&
                              `${selectedSurvey.encroachedAreaInSqmt} sq.mt`,
                          ]
                            .filter(Boolean)
                            .join(' / ')}
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Encroachment Date:</Text>
                      <Text>
                        {selectedSurvey.encroachmentYear
                          ? (() => {
                              const date = new Date(
                                selectedSurvey.encroachmentYear,
                              );
                              date.setMinutes(date.getMinutes() + 330); // Adjust for IST
                              return `${String(date.getDate()).padStart(
                                2,
                                '0',
                              )}-${String(date.getMonth() + 1).padStart(
                                2,
                                '0',
                              )}-${date.getFullYear()}`;
                            })()
                          : 'N/A'}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Status:</Text>
                      <Text
                        fontWeight="semibold"
                        color={
                          selectedSurvey.status === 'approved'
                            ? 'green.500'
                            : selectedSurvey.status === 'rejected'
                            ? 'red.500'
                            : selectedSurvey.status === 'pending'
                            ? 'blue.500'
                            : selectedSurvey.status === 'talatiview'
                            ? 'purple.500'
                            : 'gray.500'
                        }
                      >
                        {capitalizeFirst(
                          selectedSurvey.status?.toUpperCase() || 'N/A',
                        )}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>
              </Box>

              <Box mb={6}>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  mb={4}
                  color={headingColor}
                >
                  <Icon as={FaUserShield} mr={2} />
                  Encroacher Information
                </Text>
                <Box
                  bg={sectionBoxBg}
                  p={4}
                  rounded="md"
                  shadow="md"
                  color={sectionTextColor}
                  borderColor={sectionBorderColor}
                >
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontWeight="bold">Name:</Text>
                      {selectedSurvey.status === 'pending' &&
                      comparisonData &&
                      comparisonData.encroacherName !==
                        selectedSurvey.encroacherName ? (
                        <>
                          <Text>
                            {' '}
                            {capitalizeFirst(
                              selectedSurvey.encroacherName || 'N/A',
                            )}
                          </Text>
                          {user?.role === 'tahsildar' && (
                            <Text color={previousColor}>
                              <strong>Previous:</strong>{' '}
                              {capitalizeFirst(comparisonData.encroacherName)}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Text>
                          {capitalizeFirst(
                            selectedSurvey.encroacherName || 'N/A',
                          )}
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Address:</Text>

                      {selectedSurvey.status === 'pending' &&
                      comparisonData &&
                      comparisonData.encroacherAddress !==
                        selectedSurvey.encroacherAddress ? (
                        <>
                          <Text>
                            {' '}
                            {capitalizeFirst(
                              selectedSurvey.encroacherAddress || 'N/A',
                            )}
                          </Text>
                          {user?.role === 'tahsildar' && (
                            <Text color={previousColor}>
                              <strong>Previous:</strong>{' '}
                              {capitalizeFirst(
                                comparisonData.encroacherAddress,
                              )}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Text>
                          {capitalizeFirst(
                            selectedSurvey.encroacherAddress || 'N/A',
                          )}
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Caste:</Text>

                      {selectedSurvey.status === 'pending' &&
                      comparisonData &&
                      comparisonData.caste !== selectedSurvey.caste ? (
                        <>
                          <Text>
                            {' '}
                            {capitalizeFirst(selectedSurvey.caste || 'N/A')}
                          </Text>
                          {user?.role === 'tahsildar' && (
                            <Text color={previousColor}>
                              <strong>Previous:</strong>{' '}
                              {capitalizeFirst(comparisonData.caste)}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Text>
                          {capitalizeFirst(selectedSurvey.caste || 'N/A')}
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Text fontWeight="bold">BPL Family:</Text>
                      {selectedSurvey.status === 'pending' &&
                      comparisonData &&
                      comparisonData.isBPLFamily !==
                        selectedSurvey.isBPLFamily ? (
                        <>
                          <Text>
                            {selectedSurvey.isBPLFamily ? 'Yes' : 'No'}
                          </Text>
                          {user?.role === 'tahsildar' && (
                            <Text color={previousColor}>
                              <strong>Previous:</strong>{' '}
                              {comparisonData.isBPLFamily ? 'Yes' : 'No'}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Text>
                          {comparisonData
                            ? comparisonData.isBPLFamily
                              ? 'Yes'
                              : 'No'
                            : selectedSurvey.isBPLFamily
                            ? 'Yes'
                            : 'No'}
                        </Text>
                      )}
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Annual Income:</Text>
                      <Text>
                        {(() => {
                          const cleaned = selectedSurvey.annualIncome?.replace(
                            /[^\d]/g,
                            '',
                          );
                          const num = Number(cleaned);
                          if (isNaN(num)) {
                            return `${
                              selectedSurvey.annualIncome || 'N/A'
                            } (Invalid Format)`;
                          }
                          return `₹${num.toLocaleString('en-IN')} (${
                            num < 250000
                              ? 'Less than 2.5 lakh'
                              : 'Greater than 2.5 lakh'
                          })`;
                        })()}
                      </Text>

                      {/* Show previous value only if status is pending and different */}
                      {selectedSurvey.status === 'pending' &&
                        comparisonData &&
                        comparisonData.annualIncome !==
                          selectedSurvey.annualIncome &&
                        user?.role === 'tahsildar' && (
                          <Text color={previousColor}>
                            <strong>Previous:</strong>{' '}
                            {(() => {
                              const cleaned =
                                comparisonData.annualIncome?.replace(
                                  /[^\d]/g,
                                  '',
                                );
                              const num = Number(cleaned);
                              if (isNaN(num)) {
                                return `${comparisonData.annualIncome} (Invalid Format)`;
                              }
                              return `₹${num.toLocaleString('en-IN')} (${
                                num < 250000
                                  ? 'Less than 2.5 lakh'
                                  : 'Greater than 2.5 lakh'
                              })`;
                            })()}
                          </Text>
                        )}
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Social Category Details:</Text>

                      {selectedSurvey.status === 'pending' &&
                      comparisonData &&
                      comparisonData.socialCategoryDetails !==
                        selectedSurvey.socialCategoryDetails ? (
                        <>
                          <Text>
                            {capitalizeFirst(
                              selectedSurvey.socialCategoryDetails || 'N/A',
                            )}
                          </Text>
                          {user?.role === 'tahsildar' && (
                            <Text color={previousColor}>
                              <strong>Previous:</strong>{' '}
                              {capitalizeFirst(
                                comparisonData.socialCategoryDetails || 'N/A',
                              )}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Text>
                          {capitalizeFirst(
                            selectedSurvey.socialCategoryDetails || 'N/A',
                          )}
                        </Text>
                      )}
                    </Box>

                    {selectedSurvey.socialCategory &&
                      Object.entries(selectedSurvey.socialCategory)
                        .filter(([_, value]) => value && value !== '-')
                        .map(([key, value]) => (
                          <Box key={key}>
                            <Text fontWeight="bold">
                              {key.replace(/([A-Z])/g, ' $1').toUpperCase()}:
                            </Text>
                            <Text>{capitalizeFirst(value || 'N/A')}</Text>
                          </Box>
                        ))}
                  </SimpleGrid>
                </Box>
              </Box>

              {selectedSurvey.typeOfEncroachment && (
                <Box mb={6}>
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    mb={4}
                    color={sectionTitleColor}
                  >
                    <Icon as={FaTools} mr={2} />
                    Type of Encroachment
                  </Text>
                  <Box
                    color={textColor}
                    bg={boxBg}
                    p={4}
                    rounded="md"
                    shadow="md"
                    borderColor={borderColor}
                  >
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      {Object.entries(selectedSurvey.typeOfEncroachment)
                        .filter(([_, value]) => value && value !== '-')
                        .map(([key, currentValue]) => {
                          const originalValue =
                            comparisonData?.typeOfEncroachment?.[key] || '';
                          const wasPresentBefore =
                            key in (comparisonData?.typeOfEncroachment || {});
                          const isChanged =
                            wasPresentBefore && originalValue !== currentValue;
                          const isNewlyAdded = !wasPresentBefore;

                          return (
                            <Box key={key}>
                              <Text fontWeight="bold">
                                {key.replace(/([A-Z])/g, ' $1').toUpperCase()}:
                              </Text>

                              {isNewlyAdded ? (
                                <Text>
                                  {capitalizeFirst(currentValue || 'N/A')}
                                </Text>
                              ) : isChanged ? (
                                <>
                                  <Text>
                                    {capitalizeFirst(originalValue || 'N/A')}
                                  </Text>
                                  {user?.role === 'tahsildar' && (
                                    <Text color={previousColor}>
                                      <strong>Previous:</strong>{' '}
                                      {capitalizeFirst(originalValue)}
                                    </Text>
                                  )}
                                </>
                              ) : (
                                <Text>
                                  {capitalizeFirst(currentValue || 'N/A')}
                                </Text>
                              )}
                            </Box>
                          );
                        })}
                    </SimpleGrid>
                  </Box>
                </Box>
              )}

              <Box mb={6}>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  mb={4}
                  color={sectionTitleColor}
                >
                  <Icon as={FaMapMarkerAlt} mr={2} />
                  Location & Documents
                </Text>
                <Box
                  bg={boxBg}
                  p={4}
                  rounded="md"
                  shadow="md"
                  color={textColor}
                >
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {selectedSurvey.imagesLocation?.coordinates?.length ===
                      2 && (
                      <Box>
                        <Text fontWeight="bold">Map Location:</Text>
                        <a
                          href={`https://www.google.com/maps?q=${selectedSurvey.imagesLocation.coordinates[1]},${selectedSurvey.imagesLocation.coordinates[0]}`}
                          target="_blank"
                          rel="noreferrer"
                          // color={mapLinkColor}
                          style={{
                            color: 'green',
                            textDecoration: 'underline',
                          }}
                        >
                          <MdLocationOn style={{ display: 'inline' }} /> View on
                          Map
                        </a>
                      </Box>
                    )}

                    <Box>
                      <Text fontWeight="bold">Document Type:</Text>

                      {comparisonData &&
                      comparisonData.documentType !==
                        selectedSurvey.documentType ? (
                        <>
                          <Text>
                            {capitalizeFirst(
                              selectedSurvey.documentType || 'N/A',
                            )}
                          </Text>

                          {selectedSurvey.status === 'pending' && (
                            <Text color={previousColor}>
                              <strong>Previous:</strong>{' '}
                              {capitalizeFirst(comparisonData.documentType)}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Text>
                          {capitalizeFirst(
                            selectedSurvey.documentType || 'N/A',
                          )}
                        </Text>
                      )}
                    </Box>

                    {hasValue(selectedSurvey.documentPhoto) && (
                      <Box>
                        <Text fontWeight="bold">
                          Document Proof of Encroachment:
                        </Text>
                        <a
                          href={
                            selectedSurvey.documentPhoto.startsWith('http')
                              ? selectedSurvey.documentPhoto
                              : `${baseUrl}/${selectedSurvey.documentPhoto}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: 'blue',
                            textDecoration: 'underline',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}
                        >
                          <MdImage /> View Document Photo
                        </a>
                      </Box>
                    )}

                    {/* Proof of BPL Family */}
                    {hasValue(selectedSurvey.uploadProof) && (
                      <Box>
                        <Text fontWeight="bold">Proof of BPL Family:</Text>
                        <a
                          href={
                            selectedSurvey.uploadProof.startsWith('http')
                              ? selectedSurvey.uploadProof
                              : `${baseUrl}/${selectedSurvey.uploadProof}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            textDecoration: 'underline',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            color: 'blue',
                          }}
                        >
                          <MdImage /> View Upload Proof
                        </a>
                      </Box>
                    )}

                    {selectedSurvey.imagesArray?.length > 0 && (
                      <Box>
                        <Text fontWeight="bold">
                          Photographic Proof of Encroachment:
                        </Text>
                        {selectedSurvey.imagesArray.map((photoUrl, index) => (
                          <Box key={index} mb={2}>
                            <a
                              href={
                                photoUrl?.startsWith('http')
                                  ? photoUrl
                                  : `${baseUrl}/${photoUrl}`
                              }
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                textDecoration: 'underline',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                color: 'blue',
                              }}
                            >
                              <MdImage /> View Image {index + 1}
                            </a>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </SimpleGrid>
                </Box>
              </Box>

              <Box mb={6}>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  mb={4}
                  color={sectionRedTitleColor}
                >
                  <Icon as={MdInfo} mr={2} />
                  Additional Information
                </Text>
                <Box
                  bg={boxBg}
                  p={4}
                  rounded="md"
                  shadow="md"
                  color={textColor}
                >
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {selectedSurvey.otherEncroachmentDetails && (
                      <Box>
                        <Text fontWeight="bold">
                          Other Encroachment Details:
                        </Text>

                        <Text>
                          {capitalizeFirst(
                            selectedSurvey.otherEncroachmentDetails,
                          )}
                        </Text>

                        {selectedSurvey.status === 'pending' &&
                          comparisonData &&
                          comparisonData.otherEncroachmentDetails !==
                            selectedSurvey.otherEncroachmentDetails &&
                          user?.role === 'tahsildar' && (
                            <Text color={previousColor}>
                              <strong>Previous:</strong>{' '}
                              {capitalizeFirst(
                                comparisonData?.otherEncroachmentDetails,
                              )}
                            </Text>
                          )}
                      </Box>
                    )}
                    <Box>
                      <Text fontWeight="bold">Number of Family Members:</Text>

                      <Text>
                        {capitalizeFirst(selectedSurvey?.noOfFamilyMembers)}
                      </Text>

                      {selectedSurvey.status === 'pending' &&
                        comparisonData &&
                        comparisonData?.noOfFamilyMembers !==
                          selectedSurvey?.noOfFamilyMembers &&
                        user?.role === 'tahsildar' && (
                          <Text color={previousColor}>
                            <strong>Previous:</strong>{' '}
                            {capitalizeFirst(comparisonData?.noOfFamilyMembers)}
                          </Text>
                        )}
                    </Box>

                    <Box>
                      <Text fontWeight="bold">Dependent Members:</Text>

                      <Text>
                        {capitalizeFirst(selectedSurvey?.dependentMembers)}
                      </Text>

                      {selectedSurvey.status === 'pending' &&
                        comparisonData &&
                        comparisonData?.dependentMembers !==
                          selectedSurvey?.dependentMembers &&
                        user?.role === 'tahsildar' && (
                          <Text color={previousColor}>
                            <strong>Previous:</strong>{' '}
                            {capitalizeFirst(comparisonData?.dependentMembers)}
                          </Text>
                        )}
                    </Box>

                    <Box>
                      <Text fontWeight="bold">Government Scheme Support:</Text>

                      <Text>
                        {capitalizeFirst(
                          selectedSurvey.isGovSchemeSupport
                            ? selectedSurvey.isGovSchemeSupport
                            : '',
                        )}
                      </Text>

                      {selectedSurvey.status === 'pending' &&
                        comparisonData &&
                        comparisonData.isGovSchemeSupport !==
                          selectedSurvey.isGovSchemeSupport &&
                        user?.role === 'tahsildar' && (
                          <Text color={previousColor}>
                            <strong>Previous:</strong>{' '}
                            {capitalizeFirst(
                              comparisonData?.isGovSchemeSupport
                                ? comparisonData?.isGovSchemeSupport
                                : '',
                            )}
                          </Text>
                        )}
                    </Box>

                    <Box>
                      <Text fontWeight="bold">Government Scheme Name:</Text>
                      <Text>
                        {capitalizeFirst(selectedSurvey.govSchemeName)}
                      </Text>

                      {selectedSurvey.status === 'pending' &&
                        comparisonData &&
                        comparisonData.govSchemeName !==
                          selectedSurvey.govSchemeName &&
                        user?.role === 'tahsildar' && (
                          <Text color={previousColor}>
                            <strong>Previous:</strong>{' '}
                            {capitalizeFirst(comparisonData?.govSchemeName)}
                          </Text>
                        )}
                    </Box>

                    <Box>
                      <Text fontWeight="bold">Case Filed:</Text>
                      <Text>{capitalizeFirst(selectedSurvey?.caseFiled)}</Text>

                      {selectedSurvey.status === 'pending' &&
                        comparisonData &&
                        comparisonData.caseFiled !== selectedSurvey.caseFiled &&
                        user?.role === 'tahsildar' && (
                          <Text color={previousColor}>
                            <strong>Previous: </strong>
                            {capitalizeFirst(comparisonData?.caseFiled)}
                          </Text>
                        )}
                    </Box>

                    <Box>
                      <Text fontWeight="bold">Court Case Details:</Text>
                      <Text>
                        {capitalizeFirst(selectedSurvey.courtCaseDetails)}
                      </Text>

                      {selectedSurvey.status === 'pending' &&
                        comparisonData &&
                        comparisonData.courtCaseDetails !==
                          selectedSurvey.courtCaseDetails &&
                        user?.role === 'tahsildar' && (
                          <Text color={previousColor}>
                            <strong>Previous: </strong>
                            {capitalizeFirst(comparisonData.courtCaseDetails)}
                          </Text>
                        )}
                    </Box>

                    <Box>
                      <Text fontWeight="bold">Talati Remark:</Text>
                      {selectedSurvey.status === 'pending' &&
                      comparisonData &&
                      comparisonData.talatiRemark !==
                        selectedSurvey.socialCategoryDetails ? (
                        <>
                          <Text color={previousColor}>
                            <strong>Previous:</strong>{' '}
                            {capitalizeFirst(comparisonData.talatiRemark)}
                          </Text>
                        </>
                      ) : (
                        <Text>
                          {capitalizeFirst(
                            selectedSurvey.talatiRemark || 'N/A',
                          )}
                        </Text>
                      )}
                    </Box>

                    <Box>
                      {selectedSurvey.status === 'rejected' &&
                        selectedSurvey.remark && (
                          <Box>
                            <Text fontWeight="bold">Remark:</Text>
                            <Text>
                              {capitalizeFirst(selectedSurvey.remark)}
                            </Text>
                          </Box>
                        )}

                      {selectedSurvey.status === 'rejected' &&
                        selectedSurvey.rejectionComment && (
                          <Box>
                            <Text fontWeight="bold">Rejection Comment:</Text>
                            <Text>
                              {capitalizeFirst(selectedSurvey.rejectionComment)}
                            </Text>
                          </Box>
                        )}
                    </Box>
                  </SimpleGrid>
                </Box>
              </Box>

              <Box mb={6}>
                {selectedSurvey?.encroacherSignature && (
                  <Box bg={boxBg} p={4} rounded="md" shadow="md">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Image
                          src={
                            selectedSurvey?.encroacherSignature.startsWith(
                              'http',
                            )
                              ? selectedSurvey?.encroacherSignature
                              : `${baseUrl}/${selectedSurvey?.encroacherSignature}`
                          }
                          alt="Encroacher's Signature"
                          maxH="80px"
                          mt={2}
                          border="1px solid #ccc"
                          borderRadius="md"
                        />
                        <Box
                          as="hr"
                          borderColor={hrColor}
                          maxW={'210px'}
                          mt={2}
                        />
                        <Box display="flex" alignItems="center">
                          <Text
                            fontSize="14px"
                            color={textColor}
                            // paddingLeft={'10%'}
                          >
                            Signature of Encroacher
                          </Text>
                        </Box>
                      </Box>
                    </SimpleGrid>
                  </Box>
                )}
              </Box>

              {user?.role === 'tahsildar' &&
                selectedSurvey.status === 'pending' && (
                  <Box mt={6} bg={boxBg} p={4} rounded="md" shadow="md">
                    <Text fontWeight="semibold" mb={2} color={textColor}>
                      Remark (optional for approval, required for rejection)
                    </Text>
                    <Textarea
                      placeholder="Enter remark here..."
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                      mb={4}
                      color={textColor}
                      _placeholder={{ color: placeholderColor }}
                    />
                    <Stack direction="row" spacing={4} justify="flex-end">
                      <Button
                        colorScheme="green"
                        onClick={approveSurvey}
                        isLoading={isApproving}
                        loadingText="Approving"
                      >
                        Approve
                      </Button>

                      <Button
                        colorScheme="red"
                        onClick={rejectSurvey}
                        isLoading={isRejecting}
                        loadingText="Rejecting"
                      >
                        Reject
                      </Button>
                    </Stack>
                  </Box>
                )}
            </Box>
          ) : (
            <Text>No details available.</Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SurveyDetailsModal;
